import {
  Alert,
  Box,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useEffect, useState, useMemo } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import {
  EncryptionComponent,
  getEncryptionKey,
} from '@/components/encryption/encryptionComponent';
import { AWSSubnetSelect } from '@/components/selectors/awsSubnetSelect';
import { SecurityGroupSelect } from '@/components/selectors/securityGroupSelect';
import { VpcSelect } from '@/components/selectors/vpcSelect';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import { FsxVolumeSettingsSection } from '@/components/volumes/volumeSettings';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { EonTagRemark } from './eonTagRemark';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreFSxState } from '../restoreFsxWizard';

// ONTAP volume name validation: must begin with letter/underscore, contain only alphanumeric or _, max 203 chars
const ONTAP_VOLUME_NAME_REGEX = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
const ONTAP_VOLUME_NAME_MAX_LENGTH = 203;

// ONTAP junction path validation: must begin with /
const validateJunctionPath = (path: string): string | undefined => {
  if (!path) return undefined;
  if (!path.startsWith('/')) {
    return 'Junction path must begin with a forward slash (/)';
  }
  if (path.length > 255) {
    return 'Junction path cannot exceed 255 characters';
  }
  return undefined;
};

const validateOntapVolumeName = (name: string): string | undefined => {
  if (!name) return undefined;
  if (name.length > ONTAP_VOLUME_NAME_MAX_LENGTH) {
    return `Volume name cannot exceed ${ONTAP_VOLUME_NAME_MAX_LENGTH} characters`;
  }
  if (!ONTAP_VOLUME_NAME_REGEX.test(name)) {
    return 'Volume name must begin with a letter or underscore and contain only alphanumeric characters or underscores (no hyphens or special characters)';
  }
  return undefined;
};

export const FSxConfiguration = (props: StepProps<RestoreFSxState>) => {
  const dal = useDAL();

  const [error, setError] = useState<string | undefined>();
  const [isRestoring, setIsRestoring] = useState(false);
  const [nameValidationError, setNameValidationError] = useState<
    string | undefined
  >();
  const [junctionPathError, setJunctionPathError] = useState<
    string | undefined
  >();
  const [keepOriginalVolumeSettings, setKeepOriginalVolumeSettings] =
    useState(true);

  const isOntap = props.wizardState.fileSystemType === 'ONTAP';
  const isLustre = props.wizardState.fileSystemType === 'LUSTRE';
  // Standby subnet is required only for Multi-AZ (MULTI_AZ_1, MULTI_AZ_2). Single-AZ and Lustre need exactly 1 subnet.
  const deploymentType = props.wizardState.deploymentType ?? '';
  const isSingleAZDeployment = deploymentType?.startsWith('SINGLE_AZ');
  const isSingleSubnetType = isLustre || isSingleAZDeployment;
  // Source account is required for FSx restore (no cross-account support)
  const hasSourceAccount = !!props.wizardState.restoreAccount;

  // Build AWS Backup vault info for the snapshot toolbar (FSx snapshots are stored in AWS Backup)
  const fsxProps = props.wizardState.snapshot.fsxSnapshotProperties;
  const awsBackupVaultInfo = useMemo(() => {
    if (!fsxProps) return undefined;
    return {
      cloudProvider: Provider.Aws,
      region: fsxProps.region || props.wizardState.initialRegionName,
    };
  }, [fsxProps, props.wizardState.initialRegionName]);
  const backedUpVolumes = useMemo(
    () => props.wizardState.backedUpVolumes || [],
    [props.wizardState.backedUpVolumes]
  );

  // Get the selected volume (default to first)
  const selectedVolumeIndex = props.wizardState.selectedVolumeIndex ?? 0;
  const selectedVolume = backedUpVolumes[selectedVolumeIndex];

  // Extract all unique SVMs from existing ONTAP volumes
  const availableSvms = useMemo(() => {
    if (!isOntap || !props.wizardState.existingOntapVolumes?.length) {
      return [];
    }
    const svmSet = new Set<string>();
    props.wizardState.existingOntapVolumes.forEach((vol) => {
      if (vol.storageVirtualMachineId) {
        svmSet.add(vol.storageVirtualMachineId);
      }
    });
    return Array.from(svmSet).sort();
  }, [isOntap, props.wizardState.existingOntapVolumes]);

  // Get original SVM ID from backed-up volume (match with existing volumes)
  const originalSvmId = useMemo(() => {
    if (
      !isOntap ||
      !backedUpVolumes.length ||
      !props.wizardState.existingOntapVolumes?.length
    ) {
      return undefined;
    }
    const selectedVol = backedUpVolumes[selectedVolumeIndex];
    if (!selectedVol?.volumeId) return undefined;

    // Find matching volume in existing volumes to get SVM ID
    const matchingVol = props.wizardState.existingOntapVolumes.find(
      (vol) => vol.volumeId === selectedVol.volumeId
    );
    return matchingVol?.storageVirtualMachineId;
  }, [
    isOntap,
    backedUpVolumes,
    selectedVolumeIndex,
    props.wizardState.existingOntapVolumes,
  ]);

  // Get original storage efficiency setting from existing volume
  const originalStorageEfficiency = useMemo(() => {
    if (!isOntap || !selectedVolume?.volumeId) return true;
    const existingVol = props.wizardState.existingOntapVolumes?.find(
      (v) => v.volumeId === selectedVolume.volumeId
    );
    return existingVol?.storageEfficiencyEnabled ?? true;
  }, [
    isOntap,
    selectedVolume?.volumeId,
    props.wizardState.existingOntapVolumes,
  ]);

  // Detect junction path conflicts with existing volumes
  const junctionPathConflict = useMemo(() => {
    if (
      !isOntap ||
      !props.wizardState.junctionPath ||
      !props.wizardState.existingOntapVolumes
    ) {
      return undefined;
    }
    const targetPath = props.wizardState.junctionPath;
    const conflictingVolume = props.wizardState.existingOntapVolumes.find(
      (vol) => vol.junctionPath === targetPath
    );
    if (conflictingVolume) {
      return `Junction path "${targetPath}" is already in use by volume "${conflictingVolume.name}" on the target file system`;
    }
    return undefined;
  }, [
    isOntap,
    props.wizardState.junctionPath,
    props.wizardState.existingOntapVolumes,
  ]);

  // Detect volume name conflicts with existing volumes
  const volumeNameConflict = useMemo(() => {
    if (
      !isOntap ||
      !props.wizardState.volumeName ||
      !props.wizardState.existingOntapVolumes
    ) {
      return undefined;
    }
    const targetName = props.wizardState.volumeName;
    const conflictingVolume = props.wizardState.existingOntapVolumes.find(
      (vol) => vol.name === targetName
    );
    if (conflictingVolume) {
      return `Volume name "${targetName}" already exists on the target SVM`;
    }
    return undefined;
  }, [
    isOntap,
    props.wizardState.volumeName,
    props.wizardState.existingOntapVolumes,
  ]);

  // Initialize ONTAP volume options from selected volume (only on first render)
  useEffect(() => {
    if (
      isOntap &&
      selectedVolume &&
      !props.wizardState.junctionPath &&
      !props.wizardState.volumeName
    ) {
      // Set default SVM to original if available
      const defaultSvmId =
        originalSvmId ||
        (availableSvms.length > 0 ? availableSvms[0] : undefined);
      // Find matching existing volume to get storage efficiency setting
      const existingVol = props.wizardState.existingOntapVolumes?.find(
        (v) => v.volumeId === selectedVolume.volumeId
      );

      // Check if volume name already exists, add _restored suffix if so
      const existingVolumes = props.wizardState.existingOntapVolumes || [];
      const nameExists = existingVolumes.some(
        (v) => v.name === selectedVolume.name
      );
      const initialVolumeName = nameExists ? '' : selectedVolume.name;

      // Check if junction path already exists, add _restored suffix if so
      const junctionPathExists = existingVolumes.some(
        (v) => v.junctionPath === selectedVolume.junctionPath
      );
      const initialJunctionPath = junctionPathExists
        ? ''
        : selectedVolume.junctionPath;

      props.setWizardState((state) => ({
        ...state,
        volumeName: initialVolumeName,
        junctionPath: initialJunctionPath,
        volumeSize: selectedVolume.sizeBytes,
        storageEfficiencyEnabled: existingVol?.storageEfficiencyEnabled ?? true,
        storageVirtualMachineId: state.storageVirtualMachineId || defaultSvmId,
        // Keep fileSystemName in sync for display purposes
        fileSystemName: initialVolumeName,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOntap, selectedVolume?.volumeId]);

  // Validate name when it changes for ONTAP file systems
  const handleNameChange = (name: string) => {
    props.setWizardState((state) => ({
      ...state,
      fileSystemName: name,
    }));

    if (isOntap) {
      setNameValidationError(validateOntapVolumeName(name));
    } else {
      setNameValidationError(undefined);
    }
  };

  // Handle junction path change for ONTAP restores
  const handleJunctionPathChange = (path: string) => {
    props.setWizardState((state) => ({
      ...state,
      junctionPath: path,
    }));
    setJunctionPathError(validateJunctionPath(path));
  };

  useEffect(() => {
    props.setWizardState((state) => ({
      ...state,
      securityGroupIds: [],
      subnetIds: [],
      vpc: undefined,
      regionName: state.initialRegionName,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.wizardState.restoreAccount]);

  // Auto-select restore account when accounts list becomes available
  useEffect(() => {
    if (
      !props.wizardState.restoreAccount &&
      props.wizardState.accounts?.length
    ) {
      props.setWizardState((state) => ({
        ...state,
        restoreAccount: state.accounts?.[0],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.wizardState.accounts, props.wizardState.restoreAccount]);

  // Multi-AZ FSx file systems require exactly 2 subnets in different AZs
  const preferredSubnet = props.wizardState.subnetIds[0];
  const standbySubnet = props.wizardState.subnetIds[1];

  const canGoNext = () => {
    // Must have a restore account (source account for FSx)
    if (!hasSourceAccount) {
      return false;
    }

    // ONTAP is volume-level restore (no network/encryption config needed)
    if (isOntap) {
      const volumeNameValid = !nameValidationError && !volumeNameConflict;
      const junctionPathValid = !junctionPathError && !junctionPathConflict;
      const effectiveSvmId =
        props.wizardState.storageVirtualMachineId || originalSvmId;
      const svmValid = !!effectiveSvmId;
      return (
        !isRestoring &&
        !!props.wizardState.fileSystemName &&
        volumeNameValid &&
        junctionPathValid &&
        svmValid
      );
    }

    // Subnet validation: Lustre requires 1 subnet, others require 2
    const subnetValid = isSingleSubnetType
      ? !!preferredSubnet
      : !!preferredSubnet && !!standbySubnet;

    return !(
      isRestoring ||
      !props.wizardState.regionName ||
      !props.wizardState.fileSystemName ||
      nameValidationError ||
      !subnetValid ||
      !getEncryptionKey(props.wizardState.encryption)
    );
  };

  const onNextClick = () => {
    setIsRestoring(true);
    setError(undefined);

    const tags = {
      ...(props.wizardState.keepOriginalTags
        ? props.wizardState.initialTags
        : props.wizardState.tags),
      'eon:restore': 'true',
    };

    return dal.restore.fsx
      .restore(props.wizardState.resourceId, props.wizardState.snapshot.id, {
        restoreAccountId: props.wizardState.restoreAccount!.id,
        destination: {
          region: props.wizardState.regionName || '',
          fileSystemName: props.wizardState.fileSystemName || '',
          subnetIds: props.wizardState.subnetIds,
          securityGroupIds: props.wizardState.securityGroupIds,
          encryptionKey: getEncryptionKey(props.wizardState.encryption),
          tags,
          storageCapacity: props.wizardState.storageCapacity,
          throughputCapacityMBps: props.wizardState.throughputCapacityMBps,
          // ONTAP-specific restore options
          ...(isOntap && {
            ontapRestoreOptions: {
              ...(props.wizardState.storageVirtualMachineId && {
                storageVirtualMachineId:
                  props.wizardState.storageVirtualMachineId,
              }),
              ...(props.wizardState.volumeName && {
                volumeName: props.wizardState.volumeName,
              }),
              ...(props.wizardState.junctionPath && {
                junctionPath: props.wizardState.junctionPath,
              }),
              ...(props.wizardState.volumeSize && {
                volumeSize: props.wizardState.volumeSize,
              }),
              ...(props.wizardState.storageEfficiencyEnabled !== undefined && {
                storageEfficiencyEnabled:
                  props.wizardState.storageEfficiencyEnabled,
              }),
            },
          }),
        },
      })
      .then(() =>
        props.finish(
          `Started restore job for ${props.wizardState.fileSystemName}`
        )
      )
      .catch(async (e) => {
        const errorMessage = await extractErrorMessage(e);
        setIsRestoring(false);
        setError(`Failed to restore FSx file system: ${errorMessage}`);
      });
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      hideStepper
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark />
        )
      }
      stepperLabels={props.stepperLabels}
      stepperIndex={props.stepperLabels.length - 1}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={props.back}
      canGoNext={canGoNext}
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
          awsBackupVaultInfo={awsBackupVaultInfo}
        />
      }
    >
      <Box className='mx-[40px] my-[24px]'>
        {!hasSourceAccount && (
          <Alert severity='info' className='mb-[20px]'>
            <Typography variant='body2'>
              FSx backups are stored in AWS Backup Vault in the source account
              and can only be restored to the same account.
            </Typography>
          </Alert>
        )}
        {/* Non-ONTAP: File system name + VPC */}
        {!isOntap && (
          <Stack
            direction='row'
            alignItems='flex-start'
            gap='36px'
            className='mt-[20px]'
          >
            <FormControl size='small' className='flex-1'>
              <FormLabel>File system name</FormLabel>
              <TextField
                data-testid='fileSystemNameTextField'
                size='small'
                value={props.wizardState.fileSystemName || ''}
                onChange={(event) => handleNameChange(event.target.value)}
                error={!!nameValidationError}
                helperText={nameValidationError}
                disabled={!hasSourceAccount}
              />
            </FormControl>
            <VpcSelect
              accountId={props.wizardState.restoreAccount?.id}
              regionName={props.wizardState.regionName}
              vpc={props.wizardState.vpc}
              onChange={(vpc) =>
                props.setWizardState((state) => ({
                  ...state,
                  vpc,
                  subnetIds: [],
                  securityGroupIds: [],
                }))
              }
              disabledItems={[]}
            />
          </Stack>
        )}

        {/* ONTAP-specific: Volume configuration */}
        {isOntap && backedUpVolumes.length > 0 && (
          <>
            {/* Target SVM + Volume name */}
            <Stack
              direction='row'
              alignItems='flex-start'
              gap='36px'
              className='mt-[20px]'
            >
              <FormControl size='small' className='flex-1'>
                <FormLabel>Target SVM</FormLabel>
                {availableSvms.length > 0 ? (
                  <Select
                    data-testid='svmSelect'
                    size='small'
                    value={
                      props.wizardState.storageVirtualMachineId ||
                      originalSvmId ||
                      ''
                    }
                    onChange={(event) => {
                      const value = event.target.value;
                      props.setWizardState((state) => ({
                        ...state,
                        storageVirtualMachineId: value || undefined,
                      }));
                    }}
                    displayEmpty
                  >
                    {availableSvms.map((svmId) => (
                      <MenuItem key={svmId} value={svmId}>
                        {svmId}
                        {svmId === originalSvmId && (
                          <Typography
                            component='span'
                            variant='body2'
                            color='text.secondary'
                            sx={{ ml: 1 }}
                          >
                            (original)
                          </Typography>
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  <Alert severity='warning'>
                    <Typography variant='body2'>
                      No SVMs found. The original file system may have been
                      deleted. Create a new ONTAP file system first, then try
                      again.
                    </Typography>
                  </Alert>
                )}
              </FormControl>
              <FormControl size='small' className='flex-1'>
                <FormLabel>Volume name</FormLabel>
                <TextField
                  data-testid='volumeNameTextField'
                  size='small'
                  value={props.wizardState.volumeName || ''}
                  onChange={(event) => {
                    const name = event.target.value;
                    props.setWizardState((state) => ({
                      ...state,
                      volumeName: name,
                      fileSystemName: name, // Keep in sync
                    }));
                    setNameValidationError(validateOntapVolumeName(name));
                  }}
                  error={!!nameValidationError || !!volumeNameConflict}
                  helperText={nameValidationError || volumeNameConflict}
                  placeholder='my_volume'
                  InputProps={{
                    endAdornment: props.wizardState.volumeName ? (
                      nameValidationError || volumeNameConflict ? (
                        <InputAdornment position='end'>
                          <i className='clarity-error-line text-[22px]' />
                        </InputAdornment>
                      ) : (
                        <InputAdornment position='end'>
                          <i className='material-symbols-check-circle-outline-rounded' />
                        </InputAdornment>
                      )
                    ) : undefined,
                  }}
                />
              </FormControl>
            </Stack>

            {/* Junction path */}
            <Stack
              direction='row'
              alignItems='flex-start'
              gap='36px'
              className='mt-[20px]'
            >
              <FormControl size='small' className='flex-1'>
                <FormLabel>Junction path</FormLabel>
                <TextField
                  data-testid='junctionPathTextField'
                  size='small'
                  value={props.wizardState.junctionPath || ''}
                  onChange={(event) =>
                    handleJunctionPathChange(event.target.value)
                  }
                  error={!!junctionPathError || !!junctionPathConflict}
                  helperText={junctionPathError || junctionPathConflict}
                  placeholder='/volume-name'
                  InputProps={{
                    endAdornment: props.wizardState.junctionPath ? (
                      junctionPathError || junctionPathConflict ? (
                        <InputAdornment position='end'>
                          <i className='clarity-error-line text-[22px]' />
                        </InputAdornment>
                      ) : (
                        <InputAdornment position='end'>
                          <i className='material-symbols-check-circle-outline-rounded' />
                        </InputAdornment>
                      )
                    ) : undefined,
                  }}
                />
              </FormControl>
              <Box className='flex-1' />
            </Stack>
          </>
        )}

        {/* Non-ONTAP: Security Groups + Subnets */}
        {!isOntap && (
          <>
            {/* Subnet selection: Lustre is single-AZ (1 subnet), others are multi-AZ (2 subnets) */}
            {isSingleSubnetType ? (
              /* Lustre: Security Groups + Subnet */
              <Stack
                direction='row'
                alignItems='flex-start'
                gap='36px'
                className='mt-[20px]'
              >
                <SecurityGroupSelect
                  accountId={props.wizardState.restoreAccount?.id}
                  regionName={props.wizardState.regionName}
                  securityGroupIds={props.wizardState.securityGroupIds}
                  vpc={props.wizardState.vpc || ''}
                  isSubnetIdMandatory={false}
                  isOptionalField={true}
                  onChange={(securityGroupIds) =>
                    props.setWizardState((state) => ({
                      ...state,
                      securityGroupIds: securityGroupIds ?? [],
                    }))
                  }
                />
                <FormControl size='small' className='flex-1'>
                  <FormLabel>Subnet</FormLabel>
                  <AWSSubnetSelect
                    accountId={props.wizardState.restoreAccount?.id}
                    regionName={props.wizardState.regionName}
                    subnetId={preferredSubnet}
                    vpcScope={props.wizardState.vpc || ''}
                    hideLabel
                    onChange={(subnetId) =>
                      props.setWizardState((state) => ({
                        ...state,
                        subnetIds: subnetId ? [subnetId] : [],
                      }))
                    }
                  />
                </FormControl>
              </Stack>
            ) : (
              /* Windows/OpenZFS: Security Groups + Preferred subnet, then Standby subnet */
              <>
                <Stack
                  direction='row'
                  alignItems='flex-start'
                  gap='36px'
                  className='mt-[20px]'
                >
                  <SecurityGroupSelect
                    accountId={props.wizardState.restoreAccount?.id}
                    regionName={props.wizardState.regionName}
                    securityGroupIds={props.wizardState.securityGroupIds}
                    vpc={props.wizardState.vpc || ''}
                    isSubnetIdMandatory={false}
                    isOptionalField={true}
                    onChange={(securityGroupIds) =>
                      props.setWizardState((state) => ({
                        ...state,
                        securityGroupIds: securityGroupIds ?? [],
                      }))
                    }
                  />
                  <FormControl size='small' className='flex-1'>
                    <FormLabel>Preferred subnet</FormLabel>
                    <AWSSubnetSelect
                      accountId={props.wizardState.restoreAccount?.id}
                      regionName={props.wizardState.regionName}
                      subnetId={preferredSubnet}
                      vpcScope={props.wizardState.vpc || ''}
                      hideLabel
                      excludeSubnetIds={standbySubnet ? [standbySubnet] : []}
                      onChange={(subnetId) =>
                        props.setWizardState((state) => ({
                          ...state,
                          subnetIds: subnetId
                            ? [subnetId, state.subnetIds[1]].filter(Boolean)
                            : state.subnetIds.slice(1),
                        }))
                      }
                    />
                  </FormControl>
                </Stack>
                <Stack
                  direction='row'
                  alignItems='flex-start'
                  gap='36px'
                  className='mt-[20px]'
                >
                  <FormControl size='small' className='flex-1'>
                    <FormLabel>Standby subnet</FormLabel>
                    <AWSSubnetSelect
                      accountId={props.wizardState.restoreAccount?.id}
                      regionName={props.wizardState.regionName}
                      subnetId={standbySubnet}
                      vpcScope={props.wizardState.vpc || ''}
                      hideLabel
                      excludeSubnetIds={
                        preferredSubnet ? [preferredSubnet] : []
                      }
                      onChange={(subnetId) =>
                        props.setWizardState((state) => ({
                          ...state,
                          subnetIds: subnetId
                            ? [state.subnetIds[0], subnetId].filter(Boolean)
                            : [state.subnetIds[0]].filter(Boolean),
                        }))
                      }
                    />
                  </FormControl>
                  <Box className='flex-1' /> {/* Spacer for alignment */}
                </Stack>
              </>
            )}
          </>
        )}
      </Box>

      {/* Encryption - not applicable for ONTAP (volume-level restore inherits file system encryption) */}
      {!isOntap && (
        <>
          <Divider />
          <CardContent className='px-0'>
            {props.wizardState.regionName && hasSourceAccount && (
              <EncryptionComponent
                encryption={props.wizardState.encryption}
                accountId={props.wizardState.restoreAccount!.id}
                regionName={props.wizardState.regionName}
                onChange={(encryption) =>
                  props.setWizardState((state) => ({
                    ...state,
                    encryption,
                  }))
                }
              />
            )}
          </CardContent>
        </>
      )}
      {/* Volume Settings section - only for ONTAP */}
      {isOntap && selectedVolume && (
        <>
          <Divider />
          <CardContent className='px-0'>
            <FsxVolumeSettingsSection
              initialSettings={{
                sizeBytes: selectedVolume.sizeBytes ?? 0,
                storageEfficiencyEnabled: originalStorageEfficiency,
              }}
              settings={{
                sizeBytes:
                  props.wizardState.volumeSize ?? selectedVolume.sizeBytes ?? 0,
                storageEfficiencyEnabled:
                  props.wizardState.storageEfficiencyEnabled ??
                  originalStorageEfficiency,
              }}
              onChange={(settings) => {
                props.setWizardState((state) => ({
                  ...state,
                  volumeSize: settings.sizeBytes,
                  storageEfficiencyEnabled: settings.storageEfficiencyEnabled,
                }));
              }}
              keepOriginalSettings={keepOriginalVolumeSettings}
              setKeepOriginalSettings={setKeepOriginalVolumeSettings}
            />
          </CardContent>
        </>
      )}
      <Divider />
      <CardContent className='px-0'>
        <TagsSection
          initialTags={props.wizardState.initialTags}
          tags={props.wizardState.tags}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={(value: boolean) =>
            props.setWizardState((state) => ({
              ...state,
              keepOriginalTags: value,
            }))
          }
          onChange={(tags) =>
            props.setWizardState((state) => ({ ...state, tags }))
          }
        />
      </CardContent>
    </StepContainer>
  );
};
